// src/components/QRScanner.tsx
import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { isValidKegCode, normalizeKegCode } from '../utils/kegCode';
import { CONFIRM_FRAMES, createScanConfirmer, extractKegTag } from '../utils/kegScanFilter';
// Same-tag SCAN_COOLDOWN_MS lives in kegScanFilter (shared by every scan screen).

interface QRScannerProps {
  onScan: (code: string) => void;
  onError: (error: string) => void;
  /**
   * Only accept camera reads shaped like a keg tag (EK + 5 digits), normalized
   * to uppercase. Default true – every current caller scans kegs. Set false for
   * a non-keg QR use.
   */
  kegTagsOnly?: boolean;
  /** Same read needed on this many frames in a row. Default CONFIRM_FRAMES (2). */
  confirmFrames?: number;
  /** Decode only the centre of the picture (shown by the aiming box). Default true. */
  centerCrop?: boolean;
  /**
   * 'inline' (default): video at its natural aspect, max 400px wide.
   * 'fill': video covers the parent box (object-fit: cover) — used by the
   * full-screen KegScanOverlay. The aiming box still marks exactly the decoded square.
   */
  layout?: 'inline' | 'fill';
  /** Show the built-in Manual Entry / Use Camera toggle. Default true. */
  showManualToggle?: boolean;
}

/** Side of the centred square that is decoded, as a fraction of the shorter video side. */
export const AIM_BOX_FRACTION = 0.75;

const QRScanner: React.FC<QRScannerProps> = ({
  onScan,
  onError,
  kegTagsOnly = true,
  confirmFrames = CONFIRM_FRAMES,
  centerCrop = true,
  layout = 'inline',
  showManualToggle = true,
}) => {
  const fill = layout === 'fill';
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxSize, setBoxSize] = useState<{ w: number; h: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [manualCode, setManualCode] = useState('');
  const [isManual, setIsManual] = useState(false);
  const [videoSize, setVideoSize] = useState<{ w: number; h: number } | null>(null);

  // Parents pass inline callbacks; keep the latest in refs so the camera isn't
  // restarted on every parent render.
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onScanRef.current = onScan;
    onErrorRef.current = onError;
  }, [onScan, onError]);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let rafId: number | null = null;
    let stopped = false;
    const confirm = createScanConfirmer({ requiredFrames: confirmFrames });

    const startCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (stopped) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute('playsinline', 'true');
          void videoRef.current.play();
          scan();
        }
      } catch (err) {
        console.error('QRScanner: Camera access error:', err);
        onErrorRef.current('Failed to access camera. Please allow camera permissions or use manual entry.');
        setIsManual(true);
      }
    };

    const scan = () => {
      if (!videoRef.current || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings);
      if (!context) return;

      const scanFrame = () => {
        if (stopped) return;
        const video = videoRef.current;
        if (video && video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0) {
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          setVideoSize((prev) => (prev && prev.w === vw && prev.h === vh ? prev : { w: vw, h: vh }));
          const box = boxRef.current;
          if (box) {
            const bw = box.clientWidth;
            const bh = box.clientHeight;
            setBoxSize((prev) => (prev && prev.w === bw && prev.h === bh ? prev : { w: bw, h: bh }));
          }
          const side = centerCrop ? Math.floor(Math.min(vw, vh) * AIM_BOX_FRACTION) : 0;
          const sx = centerCrop ? Math.floor((vw - side) / 2) : 0;
          const sy = centerCrop ? Math.floor((vh - side) / 2) : 0;
          const sw = centerCrop ? side : vw;
          const sh = centerCrop ? side : vh;
          if (canvas.width !== sw) canvas.width = sw;
          if (canvas.height !== sh) canvas.height = sh;
          context.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
          const imageData = context.getImageData(0, 0, sw, sh);
          let decoded: ReturnType<typeof jsQR> = null;
          try {
            decoded = jsQR(imageData.data, imageData.width, imageData.height);
          } catch {
            decoded = null; // a bad frame never stops the camera
          }
          // Non-tag reads (dirt, scratches, other codes) are ignored silently.
          const candidate = decoded
            ? kegTagsOnly
              ? extractKegTag(decoded.data)
              : decoded.data.trim() || null
            : null;
          const accepted = confirm(candidate);
          if (accepted) onScanRef.current(accepted);
        }
        rafId = requestAnimationFrame(scanFrame);
      };
      scanFrame();
    };

    if (!isManual) {
      void startCamera();
    }

    return () => {
      stopped = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [isManual, kegTagsOnly, confirmFrames, centerCrop]);

  // Aiming box: same centred square that is decoded, in % of the displayed video.
  const aimBox = (() => {
    if (!centerCrop || !videoSize) return null;
    const side = Math.min(videoSize.w, videoSize.h) * AIM_BOX_FRACTION;
    if (fill) {
      // object-fit: cover scales the video by the larger ratio and crops the
      // centre, so the decoded centre square stays centred on screen.
      if (!boxSize || !boxSize.w || !boxSize.h) return null;
      const scale = Math.max(boxSize.w / videoSize.w, boxSize.h / videoSize.h);
      const px = Math.min(side * scale, boxSize.w * 0.96, boxSize.h * 0.96);
      return { width: `${px}px`, height: `${px}px` };
    }
    return {
      width: `${(side / videoSize.w) * 100}%`,
      height: `${(side / videoSize.h) * 100}%`,
    };
  })();

  const handleManualSubmit = () => {
    const code = normalizeKegCode(manualCode);
    if (isValidKegCode(code)) {
      onScan(code);
      setManualCode('');
    } else {
      onError('Valid keg tag needed (e.g., EK09503)');
    }
  };

  if (fill) {
    return (
      <div ref={boxRef} data-testid="qr-scanner-fill" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#000' }}>
        {isManual ? (
          <div data-testid="qr-camera-unavailable" style={{ color: '#fff', padding: 16, textAlign: 'center' }}>
            Camera unavailable. Allow camera access, or type the keg tag below.
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {aimBox && (
              <div
                data-testid="qr-aim-box"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: aimBox.width,
                  height: aimBox.height,
                  border: '3px solid rgba(255, 255, 255, 0.95)',
                  borderRadius: 10,
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
                  pointerEvents: 'none',
                }}
              />
            )}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center' }}>
      {isManual ? (
        <div>
          <input
            type="text"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Enter keg tag (e.g., EK09503)"
            style={{ padding: '10px', margin: '10px', width: '200px' }}
          />
          <button
            onClick={handleManualSubmit}
            style={{
              backgroundColor: '#2196F3',
              color: '#fff',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Submit
          </button>
          <button
            onClick={() => setIsManual(false)}
            style={{
              backgroundColor: '#F86752',
              color: '#fff',
              padding: '10px 20px',
              margin: '10px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Use Camera
          </button>
        </div>
      ) : (
        <div>
          <div style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '400px', overflow: 'hidden' }}>
            <video ref={videoRef} muted playsInline style={{ width: '100%', display: 'block' }} />
            {aimBox && (
              <div
                data-testid="qr-aim-box"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: aimBox.width,
                  height: aimBox.height,
                  border: '3px solid rgba(255, 255, 255, 0.9)',
                  borderRadius: '8px',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.25)',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          {showManualToggle && <button
            onClick={() => setIsManual(true)}
            style={{
              backgroundColor: '#F86752',
              color: '#fff',
              padding: '10px 20px',
              margin: '10px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Manual Entry
          </button>}
        </div>
      )}
    </div>
  );
};

export default QRScanner;